import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { taxConfigApi, TaxConfiguration, CreateTaxConfigData, TaxBracket } from '../services/taxConfig';
import { countries, getCurrencyByCountry } from '../utils/countries';
import { format, parseISO } from 'date-fns';
import { useCompany } from '../contexts/CompanyContext';

type TaxConfigFormData = Omit<CreateTaxConfigData, 'effectiveFrom' | 'effectiveTo'> & {
  effectiveFrom?: Date;
  effectiveTo?: Date;
  useTaxBrackets: boolean;
};

const TaxConfig: React.FC = () => {
  const { baseCurrency } = useCompany();
  const [configs, setConfigs] = useState<TaxConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TaxConfiguration | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<TaxConfiguration | null>(null);

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm<TaxConfigFormData>({
    defaultValues: {
      jurisdiction: '',
      incomeTaxRate: undefined,
      employeeSocialSecurityRate: undefined,
      employerSocialSecurityRate: undefined,
      taxFreeAllowance: 0,
      currency: baseCurrency,
      effectiveFrom: undefined,
      effectiveTo: undefined,
      isActive: true,
      description: '',
      useTaxBrackets: false,
      taxBrackets: [],
    },
  });

  const { fields: bracketFields, append: appendBracket, remove: removeBracket } = useFieldArray({
    control,
    name: 'taxBrackets',
  });

  const useTaxBrackets = watch('useTaxBrackets');
  const watchedJurisdiction = watch('jurisdiction');

  useEffect(() => {
    loadConfigs();
  }, []);

  // Auto-set currency when jurisdiction changes
  useEffect(() => {
    if (watchedJurisdiction) {
      const currency = getCurrencyByCountry(watchedJurisdiction);
      setValue('currency', currency);
    }
  }, [watchedJurisdiction, setValue]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await taxConfigApi.getAll({ activeOnly: false });
      console.log('Tax configs loaded:', data);
      setConfigs(data || []);
    } catch (err: any) {
      console.error('Error loading tax configs:', err);
      setError(err.response?.data?.message || 'Failed to load tax configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config?: TaxConfiguration) => {
    if (config) {
      setEditingConfig(config);
      const hasBrackets = config.taxBrackets && config.taxBrackets.length > 0;
      reset({
        jurisdiction: config.jurisdiction,
        incomeTaxRate: config.incomeTaxRate || undefined,
        employeeSocialSecurityRate: config.employeeSocialSecurityRate || config.socialSecurityRate || undefined,
        employerSocialSecurityRate: config.employerSocialSecurityRate || undefined,
        taxFreeAllowance: config.taxFreeAllowance || 0,
        currency: config.currency || 'USD',
        effectiveFrom: config.effectiveFrom ? parseISO(config.effectiveFrom) : undefined,
        effectiveTo: config.effectiveTo ? parseISO(config.effectiveTo) : undefined,
        isActive: config.isActive !== false,
        description: config.description || '',
        useTaxBrackets: hasBrackets,
        taxBrackets: config.taxBrackets?.map(b => ({
          id: b.id,
          minIncome: b.minIncome,
          maxIncome: b.maxIncome,
          taxRate: b.taxRate,
        })) || [],
      });
    } else {
      setEditingConfig(null);
      reset({
        jurisdiction: '',
        incomeTaxRate: undefined,
        employeeSocialSecurityRate: undefined,
        employerSocialSecurityRate: undefined,
        taxFreeAllowance: 0,
        currency: baseCurrency,
        effectiveFrom: undefined,
        effectiveTo: undefined,
        isActive: true,
        description: '',
        useTaxBrackets: false,
        taxBrackets: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConfig(null);
    reset();
  };

  const onSubmit = async (data: TaxConfigFormData) => {
    try {
      setError(null);
      const submitData: CreateTaxConfigData = {
        jurisdiction: data.jurisdiction,
        taxFreeAllowance: data.taxFreeAllowance || 0,
        currency: data.currency || baseCurrency,
        effectiveFrom: data.effectiveFrom ? format(data.effectiveFrom, 'yyyy-MM-dd') : undefined,
        effectiveTo: data.effectiveTo ? format(data.effectiveTo, 'yyyy-MM-dd') : undefined,
        isActive: data.isActive !== false,
        description: data.description,
        employeeSocialSecurityRate: data.employeeSocialSecurityRate,
        employerSocialSecurityRate: data.employerSocialSecurityRate,
      };

      if (data.useTaxBrackets && data.taxBrackets && data.taxBrackets.length > 0) {
        submitData.taxBrackets = data.taxBrackets.map(b => ({
          minIncome: b.minIncome,
          maxIncome: b.maxIncome,
          taxRate: b.taxRate,
        }));
      } else {
        submitData.incomeTaxRate = data.incomeTaxRate;
      }

      if (editingConfig) {
        await taxConfigApi.update(editingConfig.id, submitData);
      } else {
        await taxConfigApi.create(submitData);
      }

      handleCloseDialog();
      loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save tax configuration');
    }
  };

  const handleDeleteClick = (config: TaxConfiguration) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;

    try {
      await taxConfigApi.delete(configToDelete.id);
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
      loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete tax configuration');
    }
  };

  const addBracket = () => {
    const lastBracket = bracketFields[bracketFields.length - 1];
    const minIncome = lastBracket ? (lastBracket.maxIncome || lastBracket.minIncome + 1) : 0;
    appendBracket({
      minIncome,
      maxIncome: undefined,
      taxRate: 0,
    });
  };

  const getCountryName = (code: string) => {
    return countries.find(c => c.code === code)?.name || code;
  };

  const isConfigActive = (config: TaxConfiguration) => {
    if (!config.isActive) return false;
    const today = new Date().toISOString().split('T')[0];
    const from = config.effectiveFrom || '';
    const to = config.effectiveTo || '';
    return (!from || from <= today) && (!to || to >= today);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Tax Configuration
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Tax Configuration
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Jurisdiction</TableCell>
              <TableCell>Tax Rate</TableCell>
              <TableCell>Social Security</TableCell>
              <TableCell>Tax-Free Allowance</TableCell>
              <TableCell>Effective Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No tax configurations found. Click "Add Tax Configuration" to create one.
                </TableCell>
              </TableRow>
            ) : (
              configs.map((config) => {
                const isActive = isConfigActive(config);
                const taxRateDisplay = config.taxBrackets && config.taxBrackets.length > 0
                  ? `${config.taxBrackets.length} brackets`
                  : `${((config.incomeTaxRate || 0) * 100).toFixed(1)}%`;

                const ssDisplay = [
                  config.employeeSocialSecurityRate && `Employee: ${(config.employeeSocialSecurityRate * 100).toFixed(1)}%`,
                  config.employerSocialSecurityRate && `Employer: ${(config.employerSocialSecurityRate * 100).toFixed(1)}%`,
                ].filter(Boolean).join(', ') || 'N/A';

                const effectivePeriod = [
                  config.effectiveFrom && format(parseISO(config.effectiveFrom), 'MMM d, yyyy'),
                  config.effectiveTo && format(parseISO(config.effectiveTo), 'MMM d, yyyy'),
                ].filter(Boolean).join(' - ') || 'No date range';

                return (
                  <TableRow key={config.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {getCountryName(config.jurisdiction)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {config.currency}
                      </Typography>
                    </TableCell>
                    <TableCell>{taxRateDisplay}</TableCell>
                    <TableCell>{ssDisplay}</TableCell>
                    <TableCell>
                      {config.currency} {config.taxFreeAllowance.toLocaleString()}
                    </TableCell>
                    <TableCell>{effectivePeriod}</TableCell>
                    <TableCell>
                      <Chip
                        label={isActive ? 'Active' : 'Inactive'}
                        color={isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(config)}
                        aria-label="edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(config)}
                        aria-label="delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingConfig ? 'Edit Tax Configuration' : 'Create Tax Configuration'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Controller
                  name="jurisdiction"
                  control={control}
                  rules={{ required: 'Jurisdiction is required' }}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={countries}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                      value={countries.find(c => c.code === field.value) || null}
                      onChange={(_, newValue) => {
                        field.onChange(newValue ? newValue.code : '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Jurisdiction (Country)"
                          error={!!errors.jurisdiction}
                          helperText={errors.jurisdiction?.message}
                          required
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('currency')}
                  label="Currency"
                  fullWidth
                  disabled
                  helperText="Auto-set based on jurisdiction"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('taxFreeAllowance', { valueAsNumber: true, min: 0 })}
                  label="Tax-Free Allowance"
                  type="number"
                  fullWidth
                  error={!!errors.taxFreeAllowance}
                  helperText={errors.taxFreeAllowance?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Controller
                      name="useTaxBrackets"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  }
                  label="Use Progressive Tax Brackets (instead of flat rate)"
                />
              </Grid>

              {!useTaxBrackets ? (
                <Grid item xs={12}>
                  <TextField
                    {...register('incomeTaxRate', {
                      valueAsNumber: true,
                      min: 0,
                      max: 1,
                      validate: (value) => {
                        if (!useTaxBrackets && (value === undefined || value === null)) {
                          return 'Income tax rate is required when not using brackets';
                        }
                        return true;
                      },
                    })}
                    label="Income Tax Rate"
                    type="number"
                    fullWidth
                    inputProps={{ step: 0.0001, min: 0, max: 1 }}
                    error={!!errors.incomeTaxRate}
                    helperText={errors.incomeTaxRate?.message || 'Enter as decimal (e.g., 0.20 for 20%)'}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Tax Brackets ({bracketFields.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {bracketFields.length === 0 ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No tax brackets defined. Click "Add Bracket" to add one.
                          </Alert>
                        ) : (
                          bracketFields.map((field, index) => (
                            <Box key={field.id} mb={2} p={2} border="1px solid #e0e0e0" borderRadius={1}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    {...register(`taxBrackets.${index}.minIncome` as const, {
                                      valueAsNumber: true,
                                      required: 'Min income is required',
                                    })}
                                    label="Min Income"
                                    type="number"
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    {...register(`taxBrackets.${index}.maxIncome` as const, {
                                      valueAsNumber: true,
                                    })}
                                    label="Max Income (leave empty for no limit)"
                                    type="number"
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                  <TextField
                                    {...register(`taxBrackets.${index}.taxRate` as const, {
                                      valueAsNumber: true,
                                      required: 'Tax rate is required',
                                      min: 0,
                                      max: 1,
                                    })}
                                    label="Tax Rate"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    inputProps={{ step: 0.0001, min: 0, max: 1 }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                  <IconButton
                                    onClick={() => removeBracket(index)}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Box>
                          ))
                        )}
                        <Button
                          variant="outlined"
                          onClick={addBracket}
                          startIcon={<AddIcon />}
                          size="small"
                        >
                          Add Bracket
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Social Security Contributions
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('employeeSocialSecurityRate', {
                    valueAsNumber: true,
                    min: 0,
                    max: 1,
                  })}
                  label="Employee Social Security Rate"
                  type="number"
                  fullWidth
                  inputProps={{ step: 0.0001, min: 0, max: 1 }}
                  error={!!errors.employeeSocialSecurityRate}
                  helperText="Enter as decimal (e.g., 0.10 for 10%)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('employerSocialSecurityRate', {
                    valueAsNumber: true,
                    min: 0,
                    max: 1,
                  })}
                  label="Employer Social Security Rate"
                  type="number"
                  fullWidth
                  inputProps={{ step: 0.0001, min: 0, max: 1 }}
                  error={!!errors.employerSocialSecurityRate}
                  helperText="Enter as decimal (e.g., 0.10 for 10%)"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Effective Dates
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="effectiveFrom"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Effective From"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
                      onChange={(e) => {
                        field.onChange(e.target.value ? new Date(e.target.value) : undefined);
                      }}
                      helperText="Leave empty for no start date"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="effectiveTo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Effective To"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
                      onChange={(e) => {
                        field.onChange(e.target.value ? new Date(e.target.value) : undefined);
                      }}
                      helperText="Leave empty for no end date"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description')}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  helperText="Optional description or notes about this tax configuration"
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={field.value} onChange={field.onChange} />}
                      label="Active"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingConfig ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Tax Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tax configuration for{' '}
            <strong>{configToDelete && getCountryName(configToDelete.jurisdiction)}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaxConfig;
